import * as React from "react";

import { Button } from "./button";
import { EmptyState, type EmptyStateProps } from "./empty-state";
import { Spinner } from "./spinner";

/** Backoff between automatic retries; the last step repeats. */
export const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 30_000];

type ReconnectingStateProps = Pick<EmptyStateProps, "layout" | "className"> & {
  /** Re-runs the failed read. Called on a backoff and when the tab comes back online. */
  onRetry: () => unknown;
  description?: React.ReactNode;
};

/**
 * Shown in place of an empty state when a read failed for a transient reason
 * (offline, 429, 502–504), so a failed fetch never reads as "nothing here".
 */
function ReconnectingState({
  onRetry,
  description = "Couldn't reach CaseBlink. Trying again.",
  layout,
  className
}: ReconnectingStateProps) {
  const retry = React.useRef(onRetry);
  React.useEffect(() => {
    retry.current = onRetry;
  });

  React.useEffect(() => {
    let attempt = 0;
    let timer: number | undefined;
    const schedule = () => {
      const delay =
        RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
      timer = window.setTimeout(() => {
        attempt += 1;
        void retry.current();
        schedule();
      }, delay);
    };
    const onOnline = () => void retry.current();
    schedule();
    window.addEventListener("online", onOnline);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  return (
    <EmptyState
      data-testid="reconnecting-state"
      media={<Spinner className="size-5 text-fg-3" />}
      title="Reconnecting…"
      description={description}
      action={
        <Button variant="secondary" size="sm" onClick={() => void onRetry()}>
          Retry Now
        </Button>
      }
      layout={layout}
      className={className}
    />
  );
}

export { ReconnectingState };
export type { ReconnectingStateProps };
